import { useState, useCallback, useRef } from 'react';
import { apiService } from '../../../services/api';

// Cap how many widget queries run at once. Each is an agent run (LLM, seconds),
// and a dashboard mounts every card at once, so without a gate an N-widget page
// fires N concurrent agent runs per load, per viewer (PR #165 review §4). Excess
// queries queue; the card shows its stored value as first paint while it waits.
const MAX_CONCURRENT_WIDGET_QUERIES = 3;
let activeQueryCount = 0;
const queryWaiters = [];

function acquireQuerySlot() {
  if (activeQueryCount < MAX_CONCURRENT_WIDGET_QUERIES) {
    activeQueryCount += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => queryWaiters.push(resolve));
}

function releaseQuerySlot() {
  const next = queryWaiters.shift();
  if (next) {
    next(); // hand the slot straight to the next waiter (count stays the same)
  } else {
    activeQueryCount = Math.max(0, activeQueryCount - 1);
  }
}

// --- quiescence -------------------------------------------------------------
//
// A dashboard load is not one burst, it is waves: three queries run at a time and
// each is an agent run, so the last card can settle many seconds after the first.
// Anything batching widget failures on a fixed timer therefore fires several times
// per load and, with one notification slot, each toast overwrites the last. This
// tells subscribers when the load is actually over.
const quiescentListeners = new Set();

// **Counted at REQUEST time, not at slot acquisition.** `activeQueryCount` tops out
// at MAX_CONCURRENT_WIDGET_QUERIES, so it returns to zero every time the in-flight
// three finish before the next three are granted -- several times during one load.
// Batching on that signal produced five toasts for eighteen failures, measured in
// the browser, which is the bug it was supposed to fix one layer further in.
//
// What "the load is over" actually means is that nothing has ASKED for a query and
// not yet finished, queued ones included. That is this counter.
//
// The grace window stays, for the smaller case it does cover: a card whose effect
// fires a tick or two after its neighbours would otherwise split the batch in two.
const QUIESCENT_GRACE_MS = 1200;
let requestedQueryCount = 0;
let quiescentTimer = null;

function beginQuery() {
  requestedQueryCount += 1;
  cancelQuiescent();  // whatever we thought had settled has not
}

function endQuery() {
  requestedQueryCount = Math.max(0, requestedQueryCount - 1);
  if (requestedQueryCount === 0) scheduleQuiescent();
}

function cancelQuiescent() {
  if (quiescentTimer) clearTimeout(quiescentTimer);
  quiescentTimer = null;
}

function scheduleQuiescent() {
  cancelQuiescent();
  quiescentTimer = setTimeout(() => {
    quiescentTimer = null;
    if (requestedQueryCount === 0) notifyQuiescent();
  }, QUIESCENT_GRACE_MS);
}

function notifyQuiescent() {
  // Copied before iterating: a listener may unsubscribe itself on being called.
  [...quiescentListeners].forEach((fn) => {
    try {
      fn();
    } catch {
      // A failing subscriber must not strand the others, or the next load's slots.
    }
  });
}

/** Call `fn` each time the last in-flight widget query finishes. Returns an unsubscribe. */
export function onWidgetQueriesQuiescent(fn) {
  quiescentListeners.add(fn);
  return () => quiescentListeners.delete(fn);
}

/**
 * Clear all module-level query accounting.
 *
 * Called on Insights unmount, and by tests. Not only a test seam: `beginQuery` and
 * `endQuery` pair inside one `executeQuery` call, so a query still QUEUED when the
 * page is left never resolves its slot, never reaches its `finally`, and leaves the
 * count above zero for the rest of the session. Quiescence would then never fire
 * again and every later failure toast would wait out the batcher's backstop instead.
 */
export function resetWidgetQueryState() {
  activeQueryCount = 0;
  requestedQueryCount = 0;
  queryWaiters.length = 0;
  quiescentListeners.clear();
  cancelQuiescent();
}

// Deliberately only 403. A denial is the one HTTP failure the card already has a
// distinct, correct treatment for -- its own sentence, and no Retry, because
// retrying cannot grant access.
//
// Everything else stays 'network_error'. That is not laziness: a 404 here means the
// route or the widget is missing, NOT that the query found nothing, and mapping it
// to 'empty_result' would print "No matching records" over a routing bug. Inventing
// a kind we are not sure of is worse than the honest "something went wrong".
function errorTypeForStatus(status) {
  return status === 403 ? 'permission_denied' : 'network_error';
}

/**
 * useWidgetQuery — manages the lifecycle of a widget query.
 * Returns: { status, data, error, errorType, metadata, executeQuery, reset }
 */
export function useWidgetQuery() {
  const [status, setStatus] = useState('idle');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [metadata, setMetadata] = useState(null);
  // Per-result extras the backend attaches to a failure (e.g. the column headers
  // of a table that matched nothing), so a state can still show structure.
  const [details, setDetails] = useState(null);
  // Monotonic id per executeQuery call. An edit re-fires the query while an earlier one
  // for the OLD prompt/config may still be in flight; without this the older response
  // could resolve last and overwrite liveData — which Insights then PUTs back, persisting
  // data that no longer matches the widget (review on #182). Only the latest call writes.
  const genRef = useRef(0);

  const executeQuery = useCallback(async (widgetTypeId, prompt, datasetIds = [], timeRange, opts = {}) => {
    const gen = (genRef.current += 1);
    // Registered here, synchronously, BEFORE the slot is awaited: a queued query is
    // still an outstanding one, and the batcher must not treat the load as finished
    // while fifteen cards are waiting their turn.
    beginQuery();
    setStatus('loading');
    setError(null);
    setErrorType(null);
    setDetails(null);
    // Keep the last data during a refresh so the card doesn't blank; the render
    // decides whether to show it (R5). Only clear metadata, which is per-result.
    setMetadata(null);

    await acquireQuerySlot();
    try {
      const result = await apiService.executeWidgetQuery(widgetTypeId, prompt, datasetIds, timeRange, opts);

      // A newer query superseded this one while it was in flight — drop this result.
      if (gen !== genRef.current) return;

      if (result.status === 'success') {
        setStatus('success');
        setData(result.data);
        setMetadata({ ...result.metadata, raw_response: result.raw_response });
      } else {
        // Carry the backend's classification so the card can distinguish an
        // empty result / disabled feature / truncation from a hard error
        // (PR #165 review §1). setData(null) so `data` reflects THIS result;
        // the card falls back to the widget's stored value for display.
        setStatus('error');
        setData(null);
        // The backend owns this copy: every failure it classifies now carries a
        // sentence written for a reader. This is only the last resort for a result
        // that classified a failure without one, so it must not make a claim of its
        // own -- "Query returned no results" told the reader the query succeeded and
        // found nothing, which is the wrong story for a timeout, an agent failure or
        // a 500, and those are most of what reaches here. The wording mirrors
        // QUERY_FAILED in the backend's widget_messages catalogue, which owns this
        // copy; keep the two in step.
        setError(result.message || 'The widget query could not be completed. Try again.');
        setErrorType(result.error_type || null);
        setDetails(result.details || null);
        setMetadata(result.metadata || null);
      }
    } catch (err) {
      if (gen !== genRef.current) return;
      setStatus('error');
      setData(null);
      setError(err.message || 'Network error. Please try again.');
      // Classified by status, not lumped under 'network_error'. A 403 from the
      // dataset guard is a denial, and calling it a network error made the card show
      // the true sentence while the notification said "couldn't load" -- next to a
      // Retry button that cannot help, since retrying never grants access.
      setErrorType(errorTypeForStatus(err.status));
    } finally {
      releaseQuerySlot();
      endQuery();
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setData(null);
    setError(null);
    setErrorType(null);
    setDetails(null);
    setMetadata(null);
  }, []);

  return { status, data, error, errorType, details, metadata, executeQuery, reset };
}
